import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "@/api/client";
import {
  downloadAuditCsv,
  fetchAuditActions,
  fetchAuditLog,
  type AuditFilters,
} from "@/api/endpoints";
import { InfoTip } from "@/components/InfoTip";
import { formatDateTime } from "@/lib/format";
import type { AuditLogEntry } from "@/types/api";
import { usePageTitle } from "@/hooks/usePageTitle";

const PAGE = 100;

export function ActivityLog(): JSX.Element {
  usePageTitle("Activity log");
  const [rows, setRows] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFilters] = useState<AuditFilters>({});
  const [exporting, setExporting] = useState(false);
  const actions = useQuery({ queryKey: ["audit-actions"], queryFn: fetchAuditActions });
  // Monotonic sequence: a slow earlier response must not overwrite a newer one.
  const loadSeq = useRef(0);

  const load = useCallback(
    async (offset: number): Promise<void> => {
      const seq = ++loadSeq.current;
      setLoading(true);
      setError(null);
      try {
        const page = await fetchAuditLog(PAGE, offset, filters);
        if (seq !== loadSeq.current) return; // superseded by a newer request
        setHasMore(page.length === PAGE);
        setRows((prev) => (offset === 0 ? page : [...prev, ...page]));
      } catch (err) {
        if (seq !== loadSeq.current) return;
        setError(err instanceof ApiError ? err.message : "Failed to load activity");
      } finally {
        if (seq === loadSeq.current) setLoading(false);
      }
    },
    [filters],
  );

  // Debounced: typing in the actor filter shouldn't fire a request per keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => void load(0), 300);
    return () => window.clearTimeout(t);
  }, [load]);

  async function onExport(): Promise<void> {
    setExporting(true);
    try {
      await downloadAuditCsv(filters);
    } catch {
      setError("Export failed — try again.");
    } finally {
      setExporting(false);
    }
  }

  function setFilter<K extends keyof AuditFilters>(key: K, value: string): void {
    setFilters((f) => {
      const next = { ...f };
      if (value) next[key] = value as AuditFilters[K];
      else delete next[key];
      return next;
    });
  }

  return (
    <div className="space-y-6">
      <header className="flex items-baseline justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold inline-flex items-center">
            Activity log
            <InfoTip
              label="Activity log"
              body="An append-only record of every change made in AquaTrack — who did it, what they did, when, and from what IP. Read-only; covers logins, onboarding, rule edits, system attributes, schedule overrides, invitations, and settings."
            />
          </h1>
          <p className="text-sm text-ink-300">Most recent first.</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            className="btn-primary py-1 px-3 text-sm"
            onClick={() => void onExport()}
            disabled={exporting}
            title="Download the filtered log as CSV (newest 50,000 rows max)"
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </button>
          <button
            type="button"
            className="btn-primary py-1 px-3 text-sm"
            onClick={() => void load(0)}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </header>

      <section className="card">
        <div className="flex flex-wrap items-end gap-3 text-sm">
          <label className="space-y-1">
            <span className="block text-xs text-ink-400">Who (email contains)</span>
            <input
              type="search"
              className="input w-52 py-1"
              value={filters.actor ?? ""}
              onChange={(e) => setFilter("actor", e.target.value)}
              placeholder="anyone"
            />
          </label>
          <label className="space-y-1">
            <span className="block text-xs text-ink-400">Action</span>
            <select
              className="input w-48 py-1"
              value={filters.action ?? ""}
              onChange={(e) => setFilter("action", e.target.value)}
            >
              <option value="">All actions</option>
              {(actions.data ?? []).map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1">
            <span className="block text-xs text-ink-400">From</span>
            <input
              type="date"
              className="input w-40 py-1"
              value={filters.dateFrom ?? ""}
              onChange={(e) => setFilter("dateFrom", e.target.value)}
            />
          </label>
          <label className="space-y-1">
            <span className="block text-xs text-ink-400">To</span>
            <input
              type="date"
              className="input w-40 py-1"
              value={filters.dateTo ?? ""}
              onChange={(e) => setFilter("dateTo", e.target.value)}
            />
          </label>
          {Object.keys(filters).length > 0 && (
            <button
              type="button"
              className="text-ink-300 hover:text-ink-100 pb-1.5"
              onClick={() => setFilters({})}
            >
              Clear filters
            </button>
          )}
        </div>
      </section>

      {error && <p className="text-sm text-severity-high">{error}</p>}

      <section className="card overflow-x-auto">
        {rows.length === 0 && loading ? (
          <div className="skeleton h-48" aria-label="Loading activity" />
        ) : rows.length === 0 ? (
          <p className="text-sm text-ink-300">No activity recorded yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-ink-300 border-b border-ink-700">
              <tr>
                <th className="py-2 pr-4 font-medium">When</th>
                <th className="py-2 pr-4 font-medium">Who</th>
                <th className="py-2 pr-4 font-medium">Action</th>
                <th className="py-2 pr-4 font-medium">Result</th>
                <th className="py-2 pr-4 font-medium">Request</th>
                <th className="py-2 font-medium">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-700">
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="py-2 pr-4 text-ink-300 whitespace-nowrap">
                    {formatDateTime(r.created_at)}
                  </td>
                  <td className="py-2 pr-4">
                    {r.actor_email ?? <span className="text-ink-400">—</span>}
                    {r.actor_scope && (
                      <span className="ml-1 text-xs text-ink-400">({r.actor_scope})</span>
                    )}
                  </td>
                  <td className="py-2 pr-4">{r.action ?? `${r.method} ${r.path}`}</td>
                  <td className="py-2 pr-4">
                    <StatusPill code={r.status_code} />
                  </td>
                  <td className="py-2 pr-4 text-ink-400 font-mono text-xs">
                    {r.method} {r.path}
                  </td>
                  <td className="py-2 text-ink-400 font-mono text-xs">{r.client_ip ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {hasMore && (
        <button
          type="button"
          className="text-sm text-ink-300 hover:text-ink-100"
          onClick={() => void load(rows.length)}
          disabled={loading}
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}

function StatusPill({ code }: { code: number }): JSX.Element {
  const cls = code >= 500 ? "pill-high" : code >= 400 ? "pill-medium" : "pill-ok";
  return <span className={cls}>{code}</span>;
}
